/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react'
import * as Yup from 'yup'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import { getUserByToken, login } from '../core/_requests'
import { useAuth } from '../core/Auth'
import { Eye, EyeOff, Loader2, LogIn, AlertCircle, User, Lock } from 'lucide-react'
import axios from 'axios'

const loginSchema = Yup.object().shape({
  username: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Username is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  username: '',
  password: '',
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const { saveAuth, setCurrentUser } = useAuth()
  const [showPassword, setShowPassword] = useState(false)

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        const { data: auth } = await login(values.username, values.password)
        saveAuth(auth)
        const roleId = auth.role?.id
        const roleEntityId = auth.roleEntityId
        const roleEntityName = auth.roleEntityName
        if (roleId) {
          sessionStorage.setItem('roleId', roleId.toString())
        }
        const userName = auth.userDetails?.username
        if (userName) {
          sessionStorage.setItem('userName', userName.toString())
        }
        if (roleEntityId) {
          sessionStorage.setItem('roleEntityId', roleEntityId.toString())
        }
        if (roleEntityName) {
          sessionStorage.setItem('roleEntityName', roleEntityName.toString())
        }
        const { data: user } = await getUserByToken()
        setCurrentUser(user)
      } catch (error: any) {
        console.error(error)
        const status = axios.isAxiosError(error) ? error.response?.status : undefined
        if (status === 401) {
          saveAuth(undefined)
        }
        let message = 'The login details are incorrect'
        if (axios.isAxiosError(error)) {
          if (status === 503) {
            message = 'Our service is temporarily unavailable for maintenance. Please try again in a few minutes.'
          } else if (!error.response && error.request) {
            message = 'Our service is temporarily unavailable for maintenance. Please try again in a few minutes.'
          }
        }
        setStatus(message)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form onSubmit={formik.handleSubmit} noValidate id='kt_login_signin_form'>
      <h2 className="text-2xl font-bold text-slate-900">Welcome back</h2>
      <p className="mt-1 text-sm text-slate-500">Sign in to your ShipsEdge workspace.</p>

      <div className="mt-7 space-y-4">
        {/* Error display */}
        {formik.status && (
          <div
            role="alert"
            className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{formik.status}</span>
          </div>
        )}

        {/* Username */}
        <div>
          <label htmlFor="username" className="block text-xs font-medium text-slate-700 mb-1.5">
            Username
          </label>
          <div className="relative">
            <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              id="username"
              type="text"
              required
              autoComplete="username"
              placeholder="Enter your username"
              className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              {...formik.getFieldProps('username')}
            />
          </div>
          {formik.touched.username && formik.errors.username && (
            <p className="mt-1 text-xs text-red-600">{formik.errors.username}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="password" className="block text-xs font-medium text-slate-700">
              Password
            </label>
            <Link
              to="/auth/forgot-password"
              className="text-[11px] text-blue-600 hover:underline font-medium"
            >
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              required
              autoComplete="current-password"
              placeholder="Enter your password"
              className="w-full pl-9 pr-10 py-2.5 text-sm rounded-lg border border-slate-200 bg-white shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              {...formik.getFieldProps('password')}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {formik.touched.password && formik.errors.password && (
            <p className="mt-1 text-xs text-red-600">{formik.errors.password}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          id="loginbtn"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <LogIn className="w-4 h-4" />
          )}
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </div>

      {/* <p className="mt-7 text-center text-sm text-slate-600">
        New to ShipsEdge?{' '}
        <Link to="/auth/registration" className="text-blue-600 font-semibold hover:underline">
          Create an account
        </Link>
      </p> */}
    </form>
  )
}
